import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { UpdateBalanceDialog } from '../../../components/admin/UpdateBalanceDialog';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog';
import LoadingSpinner from '../../../components/ui/LoadingSpinner';
import {
  useBanUserMutation,
  useGetUserByIdQuery,
  useGetUserOrdersQuery,
  useGetUserTransactionsQuery,
  useSetAdminMutation,
  useUpdateUserBalanceMutation,
} from '../../../store/api/usersApi';
import type { Order, Transaction } from '../../../types/api';
import { formatAmountExact } from '../../../utils/amountFormatter';
import { formatDate, formatRelativeTime } from '../../../utils/dateFormatter';

export default function UserDetailsPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const currentLanguage = (i18n.language || 'ar') as 'ar' | 'en';

  const userId = id ? parseInt(id, 10) : 0;

  const { data, isLoading, error } = useGetUserByIdQuery(userId, {
    skip: !userId || isNaN(userId),
  });

  const [transactionsPage] = useState(1);
  const [ordersPage] = useState(1);

  const { data: transactionsData } = useGetUserTransactionsQuery(
    { id: userId, params: { page: transactionsPage, limit: 10 } },
    { skip: !userId || isNaN(userId) }
  );

  const { data: ordersData } = useGetUserOrdersQuery(
    { id: userId, params: { page: ordersPage, limit: 10 } },
    { skip: !userId || isNaN(userId) }
  );

  const [updateBalance, { isLoading: isUpdatingBalance }] = useUpdateUserBalanceMutation();
  const [banUser, { isLoading: isBanning }] = useBanUserMutation();
  const [setAdmin, { isLoading: isSettingAdmin }] = useSetAdminMutation();

  // Dialog states
  const [balanceDialog, setBalanceDialog] = useState(false);
  const [banDialog, setBanDialog] = useState(false);
  const [adminDialog, setAdminDialog] = useState(false);

  const user = data?.data?.user;

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error || !user) {
    return (
      <div className="text-center py-12">
        <p className="text-danger mb-4">
          {t('admin.users.details.error.title') || 'Error loading user'}
        </p>
        <p className="text-neutral-medium mb-6">
          {t('admin.users.details.error.notFound') || 'User not found'}
        </p>
        <Link
          to="/admin/users"
          className="text-brand-accent hover:text-brand-accent/80 font-semibold transition-colors duration-200"
        >
          {t('admin.users.details.back') || 'Back to Users'}
        </Link>
      </div>
    );
  }

  const handleUpdateBalance = async (
    amount: number,
    type: 'admin_add' | 'admin_deduct',
    notes?: string
  ) => {
    try {
      await updateBalance({
        id: userId,
        data: {
          amount,
          type,
          notes,
        },
      }).unwrap();

      toast.success(t('admin.users.balanceSuccess') || 'Balance updated successfully');
      setBalanceDialog(false);
    } catch (err) {
      const error = err as { data?: { error?: string }; message?: string };
      toast.error(error?.data?.error || error?.message || 'Failed to update balance');
    }
  };

  const handleBanUser = async () => {
    try {
      await banUser({
        id: userId,
        data: { isBanned: !user.isBanned },
      }).unwrap();

      toast.success(
        user.isBanned
          ? t('admin.users.unbanSuccess') || 'User unbanned successfully'
          : t('admin.users.banSuccess') || 'User banned successfully'
      );
      setBanDialog(false);
    } catch (err) {
      const error = err as { data?: { error?: string }; message?: string };
      toast.error(error?.data?.error || error?.message || 'Failed to update ban status');
    }
  };

  const handleSetAdmin = async () => {
    try {
      await setAdmin({
        id: userId,
        data: { isAdmin: !user.isAdmin },
      }).unwrap();

      toast.success(t('admin.users.roleSuccess') || 'User role updated successfully');
      setAdminDialog(false);
    } catch (err) {
      const error = err as { data?: { error?: string }; message?: string };
      toast.error(error?.data?.error || error?.message || 'Failed to update admin status');
    }
  };

  // Extract transactions and orders from paginated response
  const transactions: Transaction[] =
    transactionsData?.data && Array.isArray(transactionsData.data.transactions)
      ? transactionsData.data.transactions
      : [];
  const transactionsPagination = transactionsData?.data?.pagination;
  const orders: Order[] =
    ordersData?.data && Array.isArray(ordersData.data.orders) ? ordersData.data.orders : [];
  const ordersPagination = ordersData?.data?.pagination;

  return (
    <div>
      {/* Header */}
      <div
        className="mb-8 flex items-center justify-between"
        style={{
          animation: 'fadeIn 0.5s ease-out',
        }}
      >
        <div>
          <Link
            to="/admin/users"
            className="text-brand-accent hover:text-brand-accent/80 font-semibold mb-4 inline-flex items-center gap-2 transition-all duration-200 hover:underline"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            {t('admin.users.details.back') || 'Back to Users'}
          </Link>
          <h1 className="text-3xl font-heading font-bold text-brand-accent mb-2">
            {t('admin.users.details.title') || 'User Details'}
          </h1>
          <p className="text-neutral-medium">
            {user.firstName} {user.lastName || ''}
            {user.username && ` (@${user.username})`}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info Card */}
        <div className="lg:col-span-2 space-y-6">
          {/* User Information */}
          <div
            className="bg-neutral-dark/50 backdrop-blur-lg rounded-lg p-6 border border-neutral-dark/50"
            style={{
              animation: 'slideUp 0.5s ease-out 0.1s both',
            }}
          >
            <h2 className="text-xl font-heading font-bold text-brand-accent mb-6">
              {t('admin.users.details.info.title') || 'User Information'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-1">
                  {t('admin.users.details.info.telegramId') || 'Telegram ID'}
                </label>
                <p className="text-neutral-light font-mono">{user.telegramId || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-1">
                  {t('admin.users.details.info.firstName') || 'First Name'}
                </label>
                <p className="text-neutral-light">{user.firstName}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-1">
                  {t('admin.users.details.info.lastName') || 'Last Name'}
                </label>
                <p className="text-neutral-light">{user.lastName || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-1">
                  {t('admin.users.details.info.username') || 'Username'}
                </label>
                <p className="text-neutral-light">{user.username ? `@${user.username}` : '-'}</p>
              </div>
              {user.email && (
                <div>
                  <label className="block text-sm font-medium text-neutral-medium mb-1">
                    {t('admin.users.details.info.email') || 'Email'}
                  </label>
                  <p className="text-neutral-light">{user.email}</p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-1">
                  {t('admin.users.details.info.authMethod') || 'Auth Method'}
                </label>
                <p className="text-neutral-light capitalize">{user.authMethod}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-1">
                  {t('admin.users.details.info.language') || 'Language'}
                </label>
                <p className="text-neutral-light uppercase">{user.language}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-1">
                  {t('admin.users.details.info.createdAt') || 'Created At'}
                </label>
                <p className="text-neutral-light">
                  {formatDate(user.createdAt, 'PP', currentLanguage)}
                </p>
                <p className="text-xs text-neutral-medium mt-1">
                  {formatRelativeTime(user.createdAt, currentLanguage)}
                </p>
              </div>
              {user.lastActiveAt && typeof user.lastActiveAt === 'string' && (
                <div>
                  <label className="block text-sm font-medium text-neutral-medium mb-1">
                    {t('admin.users.table.lastActive') || 'Last Active'}
                  </label>
                  <p className="text-neutral-light">
                    {formatDate(user.lastActiveAt as string, 'PP', currentLanguage)}
                  </p>
                  <p className="text-xs text-neutral-medium mt-1">
                    {formatRelativeTime(user.lastActiveAt as string, currentLanguage)}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Transactions */}
          <div
            className="bg-neutral-dark/50 backdrop-blur-lg rounded-lg p-6 border border-neutral-dark/50"
            style={{
              animation: 'slideUp 0.5s ease-out 0.2s both',
            }}
          >
            <h2 className="text-xl font-heading font-bold text-brand-accent mb-4">
              {t('admin.transactions.title') || 'Recent Transactions'}
            </h2>
            {transactions.length === 0 ? (
              <p className="text-neutral-medium text-center py-4">
                {t('admin.transactions.empty') || 'No transactions found'}
              </p>
            ) : (
              <div className="space-y-2">
                {transactions.slice(0, 5).map((transaction: Transaction, index: number) => (
                  <div
                    key={transaction._id}
                    className="flex items-center justify-between p-3 bg-neutral-dark/50 rounded-lg border border-neutral-medium/50 hover:border-brand-accent/50 transition-all duration-200"
                    style={{
                      animation: `fadeIn 0.3s ease-out ${index * 50}ms both`,
                    }}
                  >
                    <div>
                      <p className="text-neutral-light font-semibold capitalize">{transaction.type}</p>
                      <p className="text-xs text-neutral-medium">
                        {formatDate(transaction.createdAt, 'PP', currentLanguage)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p
                        className={`font-semibold ${
                          transaction.type === 'recharge' || transaction.type === 'admin_add'
                            ? 'text-green-400'
                            : 'text-red-400'
                        }`}
                      >
                        {transaction.type === 'recharge' || transaction.type === 'admin_add' ? '+' : '-'}
                        ${formatAmountExact(Math.abs(transaction.amount))}
                      </p>
                      <p className="text-xs text-neutral-medium capitalize">{transaction.status}</p>
                    </div>
                  </div>
                ))}
                {transactionsPagination && transactionsPagination.total > 5 && (
                  <Link
                    to={`/admin/transactions?userId=${userId}`}
                    className="block text-center text-brand-accent hover:text-brand-accent/80 font-semibold mt-4 transition-colors duration-200"
                  >
                    {t('common.viewAll') || 'View All'} ({transactionsPagination.total})
                  </Link>
                )}
              </div>
            )}
          </div>

          {/* Orders */}
          <div
            className="bg-neutral-dark/50 backdrop-blur-lg rounded-lg p-6 border border-neutral-dark/50"
            style={{
              animation: 'slideUp 0.5s ease-out 0.3s both',
            }}
          >
            <h2 className="text-xl font-heading font-bold text-brand-accent mb-4">
              {t('admin.orders.title') || 'Recent Orders'}
            </h2>
            {orders.length === 0 ? (
              <p className="text-neutral-medium text-center py-4">
                {t('admin.orders.empty') || 'No orders found'}
              </p>
            ) : (
              <div className="space-y-2">
                {orders.slice(0, 5).map((order: Order, index: number) => (
                  <div
                    key={order._id}
                    className="flex items-center justify-between p-3 bg-neutral-dark/50 rounded-lg border border-neutral-medium/50 hover:border-brand-accent/50 transition-all duration-200"
                    style={{
                      animation: `fadeIn 0.3s ease-out ${index * 50}ms both`,
                    }}
                  >
                    <div>
                      <p className="text-neutral-light font-semibold">
                        {order.service || order.type || 'N/A'}
                      </p>
                      <p className="text-xs text-neutral-medium">
                        {formatDate(order.createdAt, 'PP', currentLanguage)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-brand-accent font-semibold">
                        ${formatAmountExact(order.finalPrice ?? 0)}
                      </p>
                      <p className="text-xs text-neutral-medium capitalize">{order.status}</p>
                    </div>
                  </div>
                ))}
                {ordersPagination && ordersPagination.total > 5 && (
                  <Link
                    to={`/admin/orders?userId=${userId}`}
                    className="block text-center text-brand-accent hover:text-brand-accent/80 font-semibold mt-4 transition-colors duration-200"
                  >
                    {t('common.viewAll') || 'View All'} ({ordersPagination.total})
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status Card */}
          <div
            className="bg-neutral-dark/50 backdrop-blur-lg rounded-lg p-6 border border-neutral-dark/50"
            style={{
              animation: 'slideUp 0.5s ease-out 0.2s both',
            }}
          >
            <h2 className="text-xl font-heading font-bold text-brand-accent mb-4">
              {t('admin.users.details.status.title') || 'Status'}
            </h2>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2">
                  {t('admin.users.details.status.accountStatus') || 'Account Status'}
                </label>
                <div className="flex gap-2">
                  {user.isBanned ? (
                    <span className="px-3 py-1 text-sm font-semibold rounded bg-red-500/20 text-red-400 border border-red-500/50">
                      {t('admin.users.status.banned') || 'Banned'}
                    </span>
                  ) : (
                    <span className="px-3 py-1 text-sm font-semibold rounded bg-green-500/20 text-green-400 border border-green-500/50">
                      {t('admin.users.status.active') || 'Active'}
                    </span>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-2">
                  {t('admin.users.details.status.role') || 'Role'}
                </label>
                <div className="flex gap-2">
                  {user.isAdmin ? (
                    <span className="px-3 py-1 text-sm font-semibold rounded bg-blue-500/20 text-blue-400 border border-blue-500/50">
                      {t('admin.users.status.admin') || 'Admin'}
                    </span>
                  ) : (
                    <span className="px-3 py-1 text-sm font-semibold rounded bg-neutral-medium/20 text-neutral-medium border border-neutral-medium/50">
                      {t('admin.users.status.regular') || 'Regular'}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Balance Card */}
          <div
            className="bg-neutral-dark/50 backdrop-blur-lg rounded-lg p-6 border border-neutral-dark/50"
            style={{
              animation: 'slideUp 0.5s ease-out 0.3s both',
            }}
          >
            <h2 className="text-xl font-heading font-bold text-brand-accent mb-4">
              {t('admin.users.details.balances.title') || 'Balance'}
            </h2>
            <div>
              <label className="block text-sm font-medium text-neutral-medium mb-1">
                {t('admin.users.details.balances.main') || 'Balance'}
              </label>
              <p className="text-3xl font-heading font-bold text-brand-accent">
                ${formatAmountExact(user.balance ?? 0)}
              </p>
            </div>
          </div>

          {/* Actions Card */}
          <div
            className="bg-neutral-dark/50 backdrop-blur-lg rounded-lg p-6 border border-neutral-dark/50"
            style={{
              animation: 'slideUp 0.5s ease-out 0.4s both',
            }}
          >
            <h2 className="text-xl font-heading font-bold text-brand-accent mb-4">
              {t('admin.users.details.actions.title') || 'Actions'}
            </h2>
            <div className="space-y-3">
              <button
                onClick={() => setBalanceDialog(true)}
                className="w-full px-4 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:bg-brand-accent/90 transition-all duration-200 hover:scale-105 active:scale-95"
              >
                {t('admin.users.actions.updateBalance') || 'Update Balance'}
              </button>
              <Link
                to={`/admin/audit-logs/${userId}`}
                className="block w-full px-4 py-2 text-center bg-blue-500/20 text-blue-400 border border-blue-500/50 font-heading font-semibold rounded-lg hover:bg-blue-500/30 transition-all duration-200 hover:scale-105 active:scale-95"
              >
                {t('admin.users.actions.viewActivityLog') || 'عرض سجل النشاط'}
              </Link>
              <button
                onClick={() => setBanDialog(true)}
                className={`w-full px-4 py-2 font-heading font-semibold rounded-lg transition-all duration-200 hover:scale-105 active:scale-95 ${
                  user.isBanned
                    ? 'bg-green-500/20 text-green-400 border border-green-500/50 hover:bg-green-500/30'
                    : 'bg-red-500/20 text-red-400 border border-red-500/50 hover:bg-red-500/30'
                }`}
              >
                {user.isBanned
                  ? t('admin.users.actions.unban') || 'Unban User'
                  : t('admin.users.actions.ban') || 'Ban User'}
              </button>
              <button
                onClick={() => setAdminDialog(true)}
                className={`w-full px-4 py-2 font-heading font-semibold rounded-lg transition-all duration-200 hover:scale-105 active:scale-95 ${
                  user.isAdmin
                    ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/50 hover:bg-yellow-500/30'
                    : 'bg-blue-500/20 text-blue-400 border border-blue-500/50 hover:bg-blue-500/30'
                }`}
              >
                {user.isAdmin
                  ? t('admin.users.actions.removeAdmin') || 'Remove Admin'
                  : t('admin.users.actions.makeAdmin') || 'Make Admin'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Update Balance Dialog */}
      <UpdateBalanceDialog
        isOpen={balanceDialog}
        onClose={() => setBalanceDialog(false)}
        onConfirm={(amount, type, notes) => {
          void handleUpdateBalance(amount, type, notes);
        }}
        isLoading={isUpdatingBalance}
      />

      {/* Ban/Unban Dialog */}
      <ConfirmDialog
        isOpen={banDialog}
        onClose={() => setBanDialog(false)}
        onConfirm={() => {
          void handleBanUser();
        }}
        title={
          user.isBanned
            ? t('admin.users.unbanTitle') || 'Unban User'
            : t('admin.users.banTitle') || 'Ban User'
        }
        message={
          user.isBanned
            ? t('admin.users.unbanConfirm', { name: `${user.firstName} ${user.lastName || ''}` }) ||
              `Are you sure you want to unban user "${user.firstName} ${user.lastName || ''}"?`
            : t('admin.users.banConfirm', { name: `${user.firstName} ${user.lastName || ''}` }) ||
              `Are you sure you want to ban user "${user.firstName} ${user.lastName || ''}"?`
        }
        confirmText={t('common.confirm') || 'Confirm'}
        type={user.isBanned ? 'info' : 'danger'}
        isLoading={isBanning}
      />

      {/* Admin Dialog */}
      <ConfirmDialog
        isOpen={adminDialog}
        onClose={() => setAdminDialog(false)}
        onConfirm={() => {
          void handleSetAdmin();
        }}
        title={
          user.isAdmin
            ? t('admin.users.removeAdminTitle') || 'Remove Admin'
            : t('admin.users.makeAdminTitle') || 'Make Admin'
        }
        message={
          user.isAdmin
            ? t('admin.users.removeAdminConfirm', { name: `${user.firstName} ${user.lastName || ''}` }) ||
              `Are you sure you want to revoke admin privileges from "${user.firstName} ${user.lastName || ''}"?`
            : t('admin.users.makeAdminConfirm', { name: `${user.firstName} ${user.lastName || ''}` }) ||
              `Are you sure you want to grant admin privileges to "${user.firstName} ${user.lastName || ''}"?`
        }
        confirmText={t('common.confirm') || 'Confirm'}
        type="warning"
        isLoading={isSettingAdmin}
      />
    </div>
  );
}
